import React from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { Reveal, useSEO } from "../components/site/Reveal";
import Newsletter from "../components/site/Newsletter";
import RelatedOrisa from "../components/site/RelatedOrisa";
import api from "../lib/api";

export default function OrisaDetail() {
  const { slug } = useParams();
  const [orisa, setOrisa] = React.useState(null);
  const [status, setStatus] = React.useState("loading"); // loading | ok | missing

  React.useEffect(() => {
    let active = true;
    setStatus("loading");
    api.get(`/orisa/${slug}`)
      .then((r) => { if (active) { setOrisa(r.data); setStatus("ok"); } })
      .catch(() => { if (active) setStatus("missing"); });
    return () => { active = false; };
  }, [slug]);

  useSEO(
    orisa ? `${orisa.name} — Òrìṣà Encyclopedia` : "Òrìṣà Encyclopedia",
    orisa ? (orisa.description || `${orisa.name}, ${orisa.domain} — a divine force of Yorùbá spirituality.`) : "",
    orisa ? orisa.image : undefined
  );

  if (status === "loading") {
    return <div className="bg-night text-cream min-h-[60vh] grid place-items-center" data-testid="orisa-detail-loading">Loading…</div>;
  }

  if (status === "missing" || !orisa) {
    return (
      <div className="bg-night text-cream min-h-[60vh] grid place-items-center px-6 text-center" data-testid="orisa-detail-missing">
        <div>
          <h1 className="font-serif text-4xl mb-4">Òrìṣà not found</h1>
          <Link to="/orisa" className="text-gold hover:underline">← Back to the Òrìṣà Encyclopedia</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-night text-cream" data-testid="orisa-detail">
      <header className="relative min-h-[62vh] flex items-end grain-overlay">
        <div className="absolute inset-0">
          {orisa.image && <img src={orisa.image} alt={orisa.name} className="w-full h-full object-cover object-top" />}
          <div className="absolute inset-0 bg-gradient-to-t from-night via-night/65 to-night/30" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 pb-14 w-full">
          <nav className="flex items-center gap-2 text-xs text-cream/70 mb-5">
            <Link to="/orisa" className="hover:text-gold">Òrìṣà Encyclopedia</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-gold-soft uppercase tracking-widest">{orisa.domain || orisa.name}</span>
          </nav>
          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="font-serif text-5xl lg:text-7xl leading-[1.02] text-shadow-hero"
            data-testid="orisa-detail-name"
          >
            {orisa.name}
          </motion.h1>
          {orisa.subtitle && <p className="mt-4 text-gold-soft text-lg">{orisa.subtitle}</p>}
          {orisa.color && <p className="mt-2 text-cream/60 text-sm uppercase tracking-widest">{orisa.color}</p>}
        </div>
      </header>

      {orisa.intro && (
        <section className="max-w-4xl mx-auto px-6 py-16">
          <Reveal>
            <p className="text-xl md:text-2xl font-serif text-cream/90 leading-relaxed border-l-2 border-gold pl-6">
              {orisa.intro}
            </p>
          </Reveal>
        </section>
      )}

      <section className="max-w-4xl mx-auto px-6 pb-20 space-y-14">
        {(orisa.sections || []).map((s, i) => (
          <Reveal key={i} delay={0.03}>
            <article data-testid={`orisa-section-${i}`}>
              {s.heading && <h2 className="font-serif text-3xl text-cream mb-4">{s.heading}</h2>}
              {s.image && (
                <img
                  src={s.image}
                  alt={s.heading || orisa.name}
                  data-testid={`orisa-section-image-${i}`}
                  className="w-full rounded-2xl border border-cream/10 mb-6 object-cover max-h-[560px]"
                />
              )}
              <div className="space-y-4">
                {String(s.body || "").split(/\n{2,}/).map((para, j) => (
                  para.trim() ? <p key={j} className="text-cream/75 leading-relaxed text-lg">{para.trim()}</p> : null
                ))}
              </div>
            </article>
          </Reveal>
        ))}
      </section>

      <RelatedOrisa currentSlug={orisa.slug} />
      <Newsletter />
    </div>
  );
}
